import React, { useEffect, useState } from 'react';
import { Plus, FolderOpen, Trash2, AlertTriangle, Copy } from 'lucide-react';
import { useProjectStore } from '../../store';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import { useAssistantContext } from '../../contexts/AssistantContext';
import VisibilityBadge from './VisibilityBadge';
import Tooltip from '../common/Tooltip';
import CloneProjectModal from './CloneProjectModal';

// AC-1599: Local type for the source-of-clone selection so we keep the
// concerns of the (Zustand) project shape and the (numeric) modal input
// distinct from the rest of this page's state.
type CloneTarget = { project_id: number; name: string };
const ProjectsPage: React.FC = () => {
  const { projects, loadProjects, createProject, setCurrentOperation, deleteProject, operationModalOpen, openOperationModal, closeOperationModal } = useProjectStore();
  const { setContext } = useAssistantContext();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // AC-1599: clone-target project (drives the CloneProjectModal). Null when
  // the modal is closed.
  const [cloneTargetProject, setCloneTargetProject] = useState<CloneTarget | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Set AssistantContext to general when viewing all projects
  useEffect(() => {
    setContext({
      contextType: 'general'
    });
  }, [setContext]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const result = await createProject({ name, description });
    setLoading(false);
    if (result.success) {
      closeOperationModal();
      setCurrentOperation(result.project);
      // Tutorial step 3 is advanced by TutorialOverlay via click trigger on btn-submit-project
      navigate(`/projects/${result.project.project_id}`);
    } else {
      setError(result.error);
    }
  };

  const handleProjectClick = (projectId: string) => {
    const project = projects.find((p) => p.project_id === projectId);
    if (project) setCurrentOperation(project);
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteProject = (project: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent project card click
    setProjectToDelete(project);
    setDeleteConfirmText('');
    setShowDeleteDialog(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteProject(projectToDelete.project_id);
      setShowDeleteDialog(false);
      setProjectToDelete(null);
    } catch (err) {
      setError('Failed to delete project.');
      console.error('Error deleting project:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Projects</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your AI agent projects
          </p>
        </div>
        <Button onClick={openOperationModal} data-tutorial="btn-create-project" className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            No projects yet
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Create your first project to start building AI workflows
          </p>
          <Button onClick={openOperationModal} data-tutorial="btn-create-project">
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.project_id}
              className="card p-6 cursor-pointer hover:shadow-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all relative group"
              onClick={() => handleProjectClick(project.project_id)}
            >
              {/* Action buttons - always visible on mobile, hover on desktop */}
              <div className="absolute top-4 right-4 flex items-center gap-1">
                {/* AC-1599: Clone / Duplicate project action */}
                <Tooltip content="Clone project">
                  <button
                    aria-label="Clone project"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCloneTargetProject({
                        project_id: parseInt(String(project.project_id), 10),
                        name: project.name,
                      });
                    }}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-200"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip content="Delete project">
                  <button
                    onClick={(e) => handleDeleteProject(project, e)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>

              <div className="flex items-center gap-2 mb-2 pr-12">
                <h2 className="text-xl font-semibold">{project.name}</h2>
                <VisibilityBadge visibility={project.visibility ?? (project.is_public ? 'public' : 'private')} />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{project.description}</p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Created {new Date(project.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && projectToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Delete Project</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete <strong>"{projectToDelete.name}"</strong>?
              </p>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                      This action will permanently delete:
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                      <li>• All workflows in this project</li>
                      <li>• All agents in this project</li>
                      <li>• All schemas in this project</li>
                      <li>• All associated data and configurations</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This action cannot be undone. Please make sure you have backed up any important data.
              </p>

              <div>
                <label htmlFor="delete-confirm-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type <strong>{projectToDelete.name}</strong> to confirm:
                </label>
                <input
                  id="delete-confirm-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={projectToDelete.name}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setProjectToDelete(null);
                  setDeleteConfirmText('');
                }}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={deleteLoading || deleteConfirmText !== projectToDelete.name}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AC-1599: Clone Project Modal */}
      {cloneTargetProject && (
        <CloneProjectModal
          isOpen={true}
          onClose={() => setCloneTargetProject(null)}
          sourceProject={cloneTargetProject}
        />
      )}

      {/* Modal for creating a new project */}
      {operationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl w-full max-w-[calc(100%-2rem)] sm:max-w-md p-6" data-tutorial="form-project">
            <h2 className="text-xl font-semibold mb-4">Create New Project</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter project name"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter project description"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeOperationModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-tutorial="btn-submit-project"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
              {error && <div className="text-red-500 mt-2 text-sm">{error}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;